import { ChatHistoryItem } from '@/pages/ISWAN_CHAT/interface';
import { timeDifferenceDescription } from '@/pages/ISWAN_CHAT/utils/timeDifferenceDescription';
import { CommentOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Conversations, ConversationsProps } from '@ant-design/x';
import { Empty, GetProp, Input, message, Modal, Space } from 'antd';
import React, { useMemo, useRef } from 'react';
import './ChatHistory.fix.css';
import s from './ChatHistory.less';

export interface ChatHistoryProps {
  chatHistory?: ChatHistoryItem[];
  activeHistory?: string;
  onActiveChange?: (e: string) => void;
  onDelete?: (conversationId: string) => void | Promise<void>;
  onRename?: (conversationId: string, newName: string) => void | Promise<void>;
}

const ChatHistory: React.FC<ChatHistoryProps> = (props: ChatHistoryProps) => {
  const { chatHistory = [], activeHistory, onActiveChange, onDelete, onRename } = props;
  const newNameRef = useRef<string>('');

  const groupConfig: GetProp<typeof Conversations, 'groupable'> = {
    sort(a, b) {
      if (a === b) return 0;
      const arrToSort = ['今天', '昨天', '7天内', '30天内', '一个月前'];
      return arrToSort.indexOf(a) - arrToSort.indexOf(b);
    },
    title: (group, { components: { GroupTitle } }) => (
      <GroupTitle>
        <Space>
          <CommentOutlined />
          <span>{group}</span>
        </Space>
      </GroupTitle>
    ),
  };

  const groupedChatHistory = useMemo(() => {
    return chatHistory.map((item) => {
      return {
        ...item,
        group: timeDifferenceDescription(item.timestamp),
      };
    });
  }, [chatHistory]);

  function handleChangeActive(key: string) {
    const item = chatHistory.find((e) => e.key === key);
    if (item === undefined) return;
    if (onActiveChange) {
      onActiveChange(item.conversationId);
    }
  }

  async function handleDelete(key: string) {
    if (onDelete) {
      await onDelete(key);
    }
  }

  async function handleRename(key: string) {
    if (!newNameRef.current.trim()) {
      message.error('请输入新的对话名称').then();
      return false;
    }
    if (onRename) {
      await onRename(key, newNameRef.current);
    }
    return true;
  }

  const menuConfig: ConversationsProps['menu'] = (e) => ({
    items: [
      {
        label: '重命名',
        key: 'rename',
        icon: <EditOutlined />,
      },
      {
        label: '删除',
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
      },
    ],
    onClick: (menuInfo: { key: any }) => {
      if (menuInfo.key === 'delete') {
        Modal.confirm({
          title: '永久删除对话',
          content: '删除后，该对话将不可恢复。确认删除吗？',
          onOk: async () => {
            await handleDelete(e.key);
          },
          onCancel: () => {},
        });
      } else if (menuInfo.key === 'rename') {
        Modal.confirm({
          title: '重命名对话',
          content: (
            <Input.TextArea
              rows={3}
              autoFocus={true}
              placeholder={'请输入新的对话名称'}
              style={{ width: '100%' }}
              onChange={(e) => (newNameRef.current = e.target.value)}
            />
          ),
          onOk: async () => {
            const result = await handleRename(e.key);
            if (!result) {
              throw new Error('重命名失败');
            }
          },
          onCancel: () => {},
          destroyOnClose: true,
        });
      }
    },
  });

  return (
    <>
      <div className={s.main + ' __fix__ChatHistory'}>
        {chatHistory.length === 0 && (
          <Empty description={'聊天历史会保存在这里'} style={{ marginBlock: '20px' }} />
        )}

        <Conversations
          menu={menuConfig}
          style={{ width: '100%', flex: 1, paddingBlock: 0 }}
          groupable={groupConfig}
          items={groupedChatHistory}
          activeKey={activeHistory}
          onActiveChange={(e) => handleChangeActive(e)}
        />
      </div>
    </>
  );
};

export default ChatHistory;
